import * as React from 'react';
import { createContext, useContext } from 'react';

import { useGateValue } from '@coursera-web/statsig';

import { isAuthenticatedUser } from 'js/lib/user';

import { CoachStateProvider } from 'bundles/unified-common/providers/CoachStateProvider';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import { useTwoPanelConversationalDiscoveryExperiment } from 'bundles/unified-description-page-common/providers/specialization/TwoPanelConversationalDiscoveryExperimentProvider';

export type CoachCareerFitAssistantContextType = {
  coachCareerFitAssistantEnabled: boolean;
  extendedCoachOnXdpRolloutEnabled: boolean;
};

export const CoachCareerFitAssistantContext = createContext<CoachCareerFitAssistantContextType>({
  coachCareerFitAssistantEnabled: false,
  extendedCoachOnXdpRolloutEnabled: false,
});

function CoachCareerFitAssistantProvider({ children }: React.PropsWithChildren) {
  const { twoPanelConversationalDiscoveryEnabled } = useTwoPanelConversationalDiscoveryExperiment();
  const rolloutEnabled = useGateValue('extend-coach-on-xdp-gate');
  // `useSpecializationPageData` only has a mounted provider on spec/prof-cert pages, so a truthy
  // value doubles as the "spec-family page" signal for the legacy fallback.
  const specializationPageData = useSpecializationPageData();

  const base = !twoPanelConversationalDiscoveryEnabled;
  const coachCareerFitAssistantEnabled = rolloutEnabled
    ? base
    : base && isAuthenticatedUser() && !!specializationPageData;

  return (
    <CoachCareerFitAssistantContext.Provider
      value={{
        coachCareerFitAssistantEnabled,
        extendedCoachOnXdpRolloutEnabled: rolloutEnabled,
      }}
    >
      {coachCareerFitAssistantEnabled ? <CoachStateProvider>{children}</CoachStateProvider> : children}
    </CoachCareerFitAssistantContext.Provider>
  );
}

export const useCoachCareerFitAssistantData = () => {
  return useContext(CoachCareerFitAssistantContext);
};

export default CoachCareerFitAssistantProvider;
